import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Chat } from '@interfaces/chat';
import { ChatMessage, MessageFileRef, PinnedMessagesResponse, SpaceTag } from '@interfaces/space';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class SpaceService {
  public currentActiveSpaceId = signal<string>('');

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
  ) {}

  getWebsocketToken = (hubId: string): Observable<any> =>
    this.http.get(this.apiService.getSpaceWebsocketTokenUrl(hubId));

  getSpaces = (uuid: string, paginationParam?: string): Observable<any> => {
    const url = paginationParam
      ? this.apiService.getUserSpacesUrl(uuid) + paginationParam
      : this.apiService.getUserSpacesUrl(uuid);
    return this.http.get(url);
  };

  createSpace = (space_data: any): Observable<any> => this.http.post(this.apiService.getSpaceUrl(), space_data);

  getSpaceRecords = (spaceId: string, paginationParam?: string): Observable<any> => {
    const url = paginationParam
      ? this.apiService.getSpaceRecordsUrl(spaceId) + paginationParam
      : this.apiService.getSpaceRecordsUrl(spaceId);
    return this.http.get(url);
  };

  getSpaceDetails = (spaceId: string): Observable<any> => this.http.get(this.apiService.getSpaceDetailsUrl(spaceId));

  sendMessage = (chatId: string, message_data: any): Observable<any> => {
    const url = /^@medullar /i.test(message_data.text)
      ? this.apiService.getChatMessagesResponseUrl(chatId)
      : this.apiService.getChatMessagesUrl(chatId);
    return this.http.post(url, message_data);
  };

  getSpacesByRecordHash = (message_data: any): Observable<any> =>
    this.http.post(this.apiService.getSpacesByRecordHashUrl(), message_data);

  createRecord = (record_data: any): Observable<any> => this.http.post(this.apiService.getRecordsUrl(), record_data);

  getChatMessages = (chatId: string, paginationParam?: string): Observable<any> => {
    const url = paginationParam
      ? this.apiService.getChatMessagesUrl(chatId) + paginationParam
      : this.apiService.getChatMessagesUrl(chatId);
    return this.http.get(url);
  };

  deleteSpaceRecord = (recordId: string, spaceId: string): Observable<any> =>
    this.http.delete(this.apiService.getSpaceRecordDetailsUrl(recordId) + '?space=' + spaceId);

  updateChat = (chatId: string, chat_data: any): Observable<any> =>
    this.http.patch(this.apiService.getChatsUrl() + chatId + '/', chat_data);

  updateSpace = (spaceId: string, space_data: any): Observable<any> =>
    this.http.patch(this.apiService.getSpaceDetailsUrl(spaceId), space_data);

  deleteSpace = (spaceId: string): Observable<any> => this.http.delete(this.apiService.getSpaceDetailsUrl(spaceId));

  getSpaceChats = (spaceId: string, paginationParam?: string): Observable<any> => {
    const url = paginationParam
      ? this.apiService.getSpaceChatsUrl(spaceId) + paginationParam
      : this.apiService.getSpaceChatsUrl(spaceId);

    return this.http.get(url);
  };

  createSpaceChat = (message_data: any): Observable<any> => this.http.post(this.apiService.getChatsUrl(), message_data);

  deleteChat = (chatId: string): Observable<any> => this.http.delete(this.apiService.getChatsUrl() + chatId + '/');

  getSpaceWithUsers = (spaceId: string): Observable<any> => this.http.get(this.apiService.getSpaceUsersUrl(spaceId));

  addUserToSpace = (spaceId: string, user_uuid: string, user_role: string): Observable<any> => {
    const post_data = {
      space: {
        uuid: spaceId,
      },
      user: {
        uuid: user_uuid,
      },
      role: user_role,
    };

    return this.http.post(this.apiService.getSpaceUsersManagementUrl(), post_data);
  };

  removeUserFromSpace = (spaceUserId: string): Observable<any> =>
    this.http.delete(this.apiService.getSpaceUsersManagementUrl() + `${spaceUserId}/`);

  updateRoleUserFromSpace = (spaceUserId: string, newRoleData: any): Observable<any> =>
    this.http.patch(this.apiService.getSpaceUsersManagementUrl() + `${spaceUserId}/`, newRoleData);

  cloneSpace = (cloneData: { space: { uuid: string }; company: { uuid: string }; name: string }): Observable<any> =>
    this.http.post(this.apiService.cloneSpaceUrl(), cloneData);

  getUserTags = (userId: string): Observable<any> => this.http.get(this.apiService.getUserTagsUrl(userId));

  createTag = (tagData: SpaceTag): Observable<any> => this.http.post(this.apiService.getTagsUrl(), tagData);

  updateTag = (tagId: string, tagData: any): Observable<any> =>
    this.http.patch(this.apiService.getTagsUrl() + `${tagId}/`, tagData);

  deleteTag = (tagId: string): Observable<any> => this.http.delete(this.apiService.getTagsUrl() + `${tagId}/`);

  setMessagePinned = (messageUuid: string, isPinned: boolean): Observable<ChatMessage | Chat> =>
    this.http.patch<ChatMessage | Chat>(this.apiService.getMessageDetailsUrl(messageUuid), {
      is_pinned: isPinned ? 'true' : 'false',
    });

  getPinnedMessages = (spaceUuid: string): Observable<PinnedMessagesResponse> =>
    this.http.get<PinnedMessagesResponse>(this.apiService.getPinnedMessagesUrl(spaceUuid));

  getMessageDiscoveryPath = (messageUuid: string, queryParams?: string): Observable<any> =>
    this.http.get(this.apiService.getMessageDetailsUrl(messageUuid) + (queryParams ? `?${queryParams}` : ''));

  getMessageToAudio = (messageId: string): Observable<any> =>
    this.http.get(this.apiService.getMessageToAudioUrl(messageId));

  getAudioToText = (spaceId: string, audioBlob: Blob): Observable<any> => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    formData.append('space_id', spaceId);

    return this.http.post(this.apiService.getAudioToTextUrl(), formData);
  };

  uploadChatFile = (chatUuid: string, file: File): Observable<MessageFileRef> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('chat', chatUuid);

    return this.http.post<MessageFileRef>(this.apiService.getChatFileUploadUrl(), formData);
  };

  updateMessage = (messageId: string, messageData: Partial<ChatMessage>): Observable<any> =>
    this.http.patch(this.apiService.getMessageDetailsUrl(messageId), messageData);

  /* Ask the agent to rewrite an existing bot message.
   * @param text - the full instruction string, e.g. `@medullar <instruction> __update__:<passage>`
   */
  getUpdatedMessageResponse = (messageId: string, text: string): Observable<{ text: string }> =>
    this.http.post<{ text: string }>(this.apiService.getUpdatedMessageResponseUrl(messageId), { text });
}
